import React from 'react';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export type CardCoverStyle = 'grid' | 'gradient' | 'plain';

export type CardCoverProps = {
	name: string;
	avatar?: React.ComponentType<{ name: string; iconUrl?: string }>;
	coverStyle?: CardCoverStyle;
	coverColor?: string;
	children?: React.ReactNode;
	badge?: React.ReactNode;
	iconUrl?: string;
	testId?: string;
};

const DEFAULT_COVER_COLOR = token('color.background.accent.green.subtlest');
const DEFAULT_GRID_LINE = token('color.border');
const DEFAULT_GRID_LINE_WIDTH = '0.3px';
const BACKGROUND_STYLES: Record<Exclude<CardCoverStyle, 'plain'>, string> = {
	grid: `linear-gradient(${DEFAULT_GRID_LINE} ${DEFAULT_GRID_LINE_WIDTH}, transparent ${DEFAULT_GRID_LINE_WIDTH}), linear-gradient(90deg, ${DEFAULT_GRID_LINE} ${DEFAULT_GRID_LINE_WIDTH}, transparent ${DEFAULT_GRID_LINE_WIDTH})`,
	gradient: 'linear-gradient(90deg, rgb(255 255 255 / 0.25) 0%, rgb(255 255 255 / 0) 70%)',
};

const styles = cssMap({
	root: {
		gridArea: 'cover',
		position: 'relative',
		width: '100%',
		height: '56px',
	},
	background: {
		position: 'absolute',
		insetBlockStart: token('space.0'),
		insetBlockEnd: token('space.200'),
		insetInlineStart: token('space.0'),
		insetInlineEnd: token('space.0'),
		width: '100%',
		height: 'auto',
	},
	gridBackground: {
		backgroundSize: '16px 16px',
		backgroundPosition: '-1px -1px',
	},
	plainRoot: {
		height: 'auto',
	},
	plainForeground: {
		paddingBlockStart: token('space.200'),
	},
	foreground: {
		boxSizing: 'border-box',
		display: 'grid',
		width: '100%',
		height: '100%',
		gridTemplateColumns: 'auto minmax(0, 1fr)',
		gridTemplateRows: 'auto',
		gridTemplateAreas: '"avatar content"',
		alignContent: 'center',
		alignItems: 'center',
		columnGap: token('space.100'),
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.250'),
	},
	avatar: {
		gridArea: 'avatar',
		alignSelf: 'center',
		justifySelf: 'center',
	},
	content: {
		gridArea: 'content',
		position: 'relative',
		zIndex: 200,
		justifySelf: 'end',
		minWidth: 0,
	},
	slot: {
		display: 'grid',
		justifyItems: 'end',
		alignItems: 'center',
	},
	layer: {
		gridArea: '1 / 1',
	},
});

const DefaultAvatar = ({
	name,
	iconUrl,
}: {
	name: string;
	iconUrl?: string;
}): React.JSX.Element => <Avatar name={name} src={iconUrl} size="small" />;

export const CardCover = ({
	name,
	avatar: AvatarComponent = DefaultAvatar,
	coverStyle = 'grid',
	coverColor,
	children,
	badge,
	iconUrl,
	testId,
}: CardCoverProps): React.JSX.Element => {
	const trailing = badge ?? children;
	const overlay = Boolean(badge && children);

	return (
		<Box xcss={cx(styles.root, coverStyle === 'plain' && styles.plainRoot)} testId={testId}>
			{coverStyle === 'plain' ? null : (
				<Box
					xcss={cx(styles.background, coverStyle === 'grid' && styles.gridBackground)}
					aria-hidden="true"
					style={{
						backgroundColor: coverColor ?? DEFAULT_COVER_COLOR,
						backgroundImage: BACKGROUND_STYLES[coverStyle],
					}}
				/>
			)}
			<Box xcss={cx(styles.foreground, coverStyle === 'plain' && styles.plainForeground)}>
				<Box xcss={styles.avatar}>
					<AvatarComponent name={name} iconUrl={iconUrl} />
				</Box>
				{overlay ? (
					<Box xcss={cx(styles.content, styles.slot)}>
						<Box xcss={styles.layer}>{badge}</Box>
						<Box xcss={styles.layer}>{children}</Box>
					</Box>
				) : trailing ? (
					<Box xcss={styles.content}>{trailing}</Box>
				) : null}
			</Box>
		</Box>
	);
};
